import React from "react";

import classNames from "classnames";
import moment from "moment-timezone";
import sortBy from "lodash/sortBy";

import Button from "ui/Button";
import PanelSection from "ui/PanelSection";
import { useSetFlash } from "providers/WithFlashes";
import { formatDateShort, formatTimestampShort } from "utils/format";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";
import { useRotateWebhookSigningKeyMutation } from "./gql/Mutation.generated";
import SigningSecret from "../SigningSecret";

export type WebhookSigningKeyType =
  OrganizationIntegrationsQuery["getWebhookSigningKeys"][number];

const REFETCH_QUERIES = ["OrganizationIntegrations"];

// Must match WebhookSigningKey::ROTATION_GRACE
const ROTATION_GRACE_DESCRIPTION = "7 days";

// Styled like a PanelItem label
const HEADER_CLASS =
  "pb-0.5 pr-6 text-left text-[12px] font-bold text-[#666679]";
const CELL_CLASS = "pb-1 pr-6";

function SigningKeys({
  organizationSlug,
  keys,
  canEdit,
}: {
  organizationSlug: string;
  keys: WebhookSigningKeyType[];
  canEdit: boolean;
}) {
  const setFlash = useSetFlash();
  const [rotateKey, rotateResult] = useRotateWebhookSigningKeyMutation({
    variables: { organizationSlug },
    refetchQueries: REFETCH_QUERIES,
    awaitRefetchQueries: true,
    onCompleted: () => setFlash("success", "Signing secret rotated"),
    onError: (error) =>
      setFlash(
        "error",
        `Could not rotate the signing secret: ${error.message}`,
      ),
  });

  const currentKeys = keys.filter((key) => key.retiredAt == null);
  // Furthest from expiring first
  const retiringKeys = sortBy(
    keys.filter((key) => key.retiredAt != null),
    (key) => -(key.retiredAt ?? 0),
  );

  const handleRotate = () => {
    if (
      window.confirm(
        "Rotate the webhook signing secret?\n\n" +
          `Requests will be signed with both the current and the new secret for ${ROTATION_GRACE_DESCRIPTION}. ` +
          "After that, only the new secret is used.",
      )
    ) {
      void rotateKey();
    }
  };
  const canRotate = canEdit && currentKeys.length > 0;

  return (
    <>
      <PanelSection>
        <div className="font-bold">Signing secret</div>
        <div className="mb-3 mt-1 text-[#606771]">
          pganalyze signs every webhook request in this organization with{" "}
          {currentKeys.length > 1 ? "each of these secrets" : "this secret"},
          following the{" "}
          <a href="https://www.standardwebhooks.com/" target="_blank">
            Standard Webhooks specification
          </a>
          . Configure your receivers to verify that requests come from
          pganalyze.
        </div>
        <SigningKeysTable signingKeys={currentKeys} canReveal={canEdit} />
        {canRotate && (
          <Button
            bare
            className="btn btn-primary mt-2"
            disabled={rotateResult.loading}
            onClick={handleRotate}
          >
            Rotate secret
          </Button>
        )}
      </PanelSection>
      {retiringKeys.length > 0 && (
        <PanelSection>
          <div className="font-bold">Retiring secrets</div>
          <div className="mb-3 mt-1 text-[#606771]">
            These are still used to sign requests until they are retired.
            Receivers can switch without disruption.
          </div>
          <SigningKeysTable
            signingKeys={retiringKeys}
            canReveal={canEdit}
            retiring
          />
        </PanelSection>
      )}
      {!canEdit && (
        <PanelSection>
          <span className="text-[#606771]">
            Only members who can edit organization settings can reveal webhook
            signing key secrets.
          </span>
        </PanelSection>
      )}
    </>
  );
}

function SigningKeysTable({
  signingKeys,
  canReveal,
  retiring = false,
}: {
  signingKeys: WebhookSigningKeyType[];
  canReveal: boolean;
  retiring?: boolean;
}) {
  // fixed width for alignment across the two instances
  return (
    <table className="w-full table-fixed">
      <thead>
        <tr>
          <th className={classNames(HEADER_CLASS, "w-[480px]")}>Secret</th>
          <th className={classNames(HEADER_CLASS, "w-[160px]")}>Created</th>
          <th className={classNames(HEADER_CLASS, retiring && "w-[240px]")}>
            Key ID
          </th>
          {retiring && <th className={HEADER_CLASS}>Retires</th>}
        </tr>
      </thead>
      <tbody>
        {signingKeys.map((key) => (
          <tr key={key.id}>
            <td className={CELL_CLASS}>
              <SigningSecret signingKeyId={key.id} canReveal={canReveal} />
            </td>
            <td className={CELL_CLASS}>
              {formatDateShort(moment.unix(key.createdAt))}
            </td>
            <td className={CELL_CLASS}>{key.keyId}</td>
            {retiring && (
              <td
                className={CELL_CLASS}
                title={`Still signs requests until ${formatTimestampShort(
                  moment.unix(key.retiredAt),
                )}`}
              >
                {formatDateShort(moment.unix(key.retiredAt))}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default SigningKeys;
